<template>
  <div class="date-info-body flex-column">
    <div class="table-heading">
      <AttensiInput
        v-model="filter"
        name="demo-search"
        :placeholder="$t('SEARCH_USERS')"
        type="search"
      />

      <BaseSegmentedButtons
        :options="tableFilterOptions"
        :active-button-index="indexOfSelectedFilterOption"
        @onSetActiveButton="handleSelectTableFilter"
      />

      <div class="table-heading__end">
        <label v-if="!isDateCancelled" class="attendee-input-wrapper"
          ><span class="attendee-input-wrapper__label"
            ><PlusIcon class="plus-icon" />
            {{ $t('product_events.ATTENDEES') }}</span
          >
          <UserSearch
            :selected-date-id="selectedDateId"
            @onSelect="handleAddUserToDate"
        /></label>
      </div>
    </div>

    <EventAttendeeTable
      :is-date-completed="
        !unwrappedDate.cancelled && unwrappedDate.state === 'expired'
      "
      :items="mappedEventRegistrations"
      :n-of-loading-items="numberOfAttendeesPerPage"
      :filter="filter"
      :is-loading="isFetching"
      @onRemove="removeUserById"
      @onCertify="handleCertifyUserById"
      @onStatusChange="handleAttendeeStatusChange"
      @onEditNote="handleEditNote"
      @onReachEndOfTable="loadMoreEventRegistrations"
    />
  </div>
</template>

<script setup lang="ts">
import { AttensiInput } from '@attensi/materia'
import { computed, ref, toRefs } from 'vue'
import { useDate } from '@/hooks/swr/useDate.js'
import { useDateUserHandler } from '@/hooks/swr/useDateUserHandler.js'
import BaseSegmentedButtons from '@/components/generics/base/BaseSegmentedButtons.vue'
import PlusIcon from '@/components/icon_components/PlusIcon.vue'
import UserSearch from '@/components/product/product_components/component_events/date_info_components/UserSearch.vue'
import { useI18n } from '@/hooks/useI18N'
import EventAttendeeTable, {
  type EventRegistrationTableEntry,
} from '@/components/tables/EventAttendeeTable.vue'
import { useToast } from '@/hooks/useToast.ts'
import { RegistrationStatus } from '@/gql/graphql.ts'
import { usePostUserProductCertification } from '@/queries/user/userMutations.ts'
import {
  useAdmitAttendeeMutation,
  useMarkAttendeeAttendedMutation,
  useMarkAttendeeNoShowMutation,
  useUpdateRegistrationMutation,
  useWaitListAttendeeMutation,
} from '@/queries/iltRegistrations/iltRegistrationsMutations.ts'
import { useIltRegistrationsInfinite } from '@/queries/iltRegistrations/iltRegistrationsQueries.ts'

const i18n = useI18n()

type EventRegistrationFilterOption = {
  display: string
  value: RegistrationStatus[] | undefined
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    selectedDateId?: number
    productId?: number
    selectedFilterValue?: RegistrationStatus[]
    indexOfSelectedFilterOption?: number
  }>(),
  {
    selectedDateId: undefined,
    productId: 0,
    selectedFilterValue: undefined,
    indexOfSelectedFilterOption: 0,
  }
)

const emit = defineEmits<{
  refreshDate: () => void
  onSelectFilter: (option: {
    index: number
    value: RegistrationStatus[] | undefined
  }) => void
}>()

const { doToast } = useToast()
const { mutate: mutateAdmitAttendee } = useAdmitAttendeeMutation()
const { mutate: mutateWaitlistAttendee } = useWaitListAttendeeMutation()
const { mutate: mutateMarkAttendeeNoShow } = useMarkAttendeeNoShowMutation()
const { mutate: mutateMarkAttendeeAttended } = useMarkAttendeeAttendedMutation()
const { mutate: mutateRegistration } = useUpdateRegistrationMutation()
const { selectedDateId, selectedFilterValue, indexOfSelectedFilterOption } =
  toRefs(props)
const filter = ref('')
const numberOfAttendeesPerPage = 20

const { unwrappedDate } = useDate(selectedDateId)
const selectedDate = computed(() => unwrappedDate.value)

const tableFilterOptions = computed<EventRegistrationFilterOption[]>(() => [
  {
    display: i18n.t('ALL'),
    value: undefined,
  },
  {
    display: i18n.t('product_events.ATTENDEES'),
    value: [RegistrationStatus.Registered, RegistrationStatus.Attended],
  },
  {
    display: i18n.t('product_events.WAITINGLIST'),
    value: [RegistrationStatus.Pending],
    disabled: !selectedDate.value.waiting_list_enabled,
  },
])

const {
  registrations: iltRegistrations,
  isFetching,
  fetchNextPage,
  hasNextPage,
} = useIltRegistrationsInfinite(
  numberOfAttendeesPerPage,
  selectedDateId,
  selectedFilterValue,
  filter
)

const loadMoreEventRegistrations = () => {
  if (hasNextPage.value && !isFetching.value) {
    fetchNextPage()
  }
}

// TODO: Refactor to our new GraphQL API when ready
const { addUserToDate, removeUserFromDate } = useDateUserHandler({
  selectedDateId,
})

const mappedEventRegistrations = computed<EventRegistrationTableEntry[]>(() => {
  return iltRegistrations.value.map((registration) => {
    return {
      id: registration.id,
      userId: registration.attendee.id,
      fullName: registration.attendee.displayName,
      status: registration.status,
      certified: registration.certified,
      department: registration.attendee.department,
      position: registration.attendee.position,
      remarks: registration.remarks,
    }
  })
})

const isDateCancelled = computed(() => selectedDate.value.state === 'canceled')

const handleSelectTableFilter = (option: {
  index: number
  value: RegistrationStatus[] | undefined
}) => {
  emit('onSelectFilter', option)
}

const handleEditNote = async (registrationId: number, note: string) => {
  mutateRegistration({ registrationId, remarks: note })
}

const handleAddUserToDate = async (user: { id: number }) => {
  await addUserToDate(user.id)
  emit('refreshDate')
}

const removeUserById = async (userId) => {
  await removeUserFromDate(userId, selectedDate.value.id)
  emit('refreshDate')
}

const { mutate: mutateUserProductCertification } =
  usePostUserProductCertification({
    onSuccess: () =>
      doToast({
        text: i18n.t('product_events.toast_messages.CERTIFY_ATTENDEE_SUCCESS'),
        theme: 'success',
      }),
    onError: () =>
      // TODO: Reset certification state on error

      doToast({
        text: i18n.t('product_events.toast_messages.CERTIFY_ATTENDEE_ERROR'),
        theme: 'error',
      }),
  })

const handleCertifyUserById = async (
  userId: number,
  registrationId: number
) => {
  return mutateUserProductCertification({
    userId,
    productId: props.productId,
    registrationId,
  })
}

const mutateAttendeeStatus: Record<
  RegistrationStatus,
  (registrationId: number) => void
> = {
  [RegistrationStatus.Registered]: mutateAdmitAttendee,
  [RegistrationStatus.Pending]: mutateWaitlistAttendee,
  [RegistrationStatus.NoShow]: mutateMarkAttendeeNoShow,
  [RegistrationStatus.Attended]: mutateMarkAttendeeAttended,
}

const handleAttendeeStatusChange = async (
  registrationId: number,
  status: RegistrationStatus
) => {
  mutateAttendeeStatus[status](registrationId)
  emit('refreshDate')
}
</script>

<style lang="scss" scoped>
.add-button-content {
  display: flex;
  justify-content: center;
}

.attendee-input-wrapper {
  display: flex;
  align-items: center;
  gap: $spacing-3xs;
  color: $color-blue-60;
  font-weight: bold;
  font-size: $font-size-s;
  padding: 2px 2px 2px $spacing-3xs;
  background-color: $color-pebble;
  border-radius: $border-radius-s;

  &__label {
    display: flex;
    align-items: center;
  }
}

.date-info-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  gap: $spacing-4xs;
}

.table-heading {
  display: flex;
  align-items: center;
  gap: $spacing-2xs;

  &__end {
    margin-left: auto;
  }
}

.plus-icon {
  width: 18px;
  height: 18px;
  margin-right: 4px;
}
</style>
